import React from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Permission Test Component
 * 
 * This component demonstrates how to use the new utility methods from AuthContext
 * that match the PHP Utility.php functions. These methods work correctly with 
 * masquerading, ensuring the React app shows the right visibility based on the
 * effective user (masqueraded user) rather than the original user.
 */
function PermissionTest() {
  const {
    // User info helpers
    getUserId,
    getUserName,
    getUserEmail,
    getFullName,
    getFirstName,
    getRole,
    getDepartment,
    
    // Role checking helpers
    isStaff,
    isSupervisor,
    isManager,
    isHod,
    isDirector,
    isApprover,
    
    // Department checking helpers
    isIT,
    isHR,
    isCallCenter,
    isProcurement,
    isFinance,
    
    // Permission helpers
    can,
    hasRole,
    
    // Masquerading state
    isMasquerading,
    originalUser,
    user
  } = useAuth();

  if (!user) {
    return <div className="p-4">Please log in to test permissions</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-2xl font-bold mb-6">Permission System Test</h1>
      
      {/* Masquerading Status */}
      {isMasquerading && (
        <div className="mb-6 p-4 bg-yellow-100 border border-yellow-400 rounded">
          <h3 className="font-semibold text-yellow-800">🎭 Masquerading Active</h3>
          <p className="text-yellow-700">
            Original User: {originalUser?.user_full_name} (ID: {originalUser?.user_id})
          </p>
          <p className="text-yellow-700">
            Acting as: {user?.user_full_name} (ID: {user?.user_id})
          </p>
        </div>
      )}
      
      {/* User Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-3">User Information</h3>
          <div className="space-y-2 text-sm">
            <p><strong>ID:</strong> {getUserId()}</p>
            <p><strong>Full Name:</strong> {getFullName()}</p>
            <p><strong>First Name:</strong> {getFirstName()}</p>
            <p><strong>Email:</strong> {getUserEmail()}</p>
            <p><strong>Role ID:</strong> {getRole()}</p>
            <p><strong>Department ID:</strong> {getDepartment()}</p>
            <p><strong>Role Name:</strong> {user?.role_name}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded">
          <h3 className="font-semibold mb-3">Organizational Roles</h3>
          <div className="space-y-2 text-sm">
            <p><strong>Staff:</strong> {isStaff() ? '✅ Yes' : '❌ No'}</p>
            <p><strong>Supervisor:</strong> {isSupervisor() ? '✅ Yes' : '❌ No'}</p>
            <p><strong>Manager:</strong> {isManager() ? '✅ Yes' : '❌ No'}</p>
            <p><strong>HOD:</strong> {isHod() ? '✅ Yes' : '❌ No'}</p>
            <p><strong>Director:</strong> {isDirector() ? '✅ Yes' : '❌ No'}</p>
            <p><strong>Approver:</strong> {isApprover() ? '✅ Yes' : '❌ No'}</p>
          </div>
        </div>
      </div>
      
      {/* Department Roles */}
      <div className="mb-8">
        <h3 className="font-semibold mb-3">Department Roles</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div className={`p-3 rounded text-center ${isIT() ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
            <div>IT Department</div>
            <div className="font-bold">{isIT() ? '✅' : '❌'}</div>
          </div>
          <div className={`p-3 rounded text-center ${isHR() ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
            <div>HR Department</div>
            <div className="font-bold">{isHR() ? '✅' : '❌'}</div>
          </div>
          <div className={`p-3 rounded text-center ${isCallCenter() ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
            <div>Call Center</div>
            <div className="font-bold">{isCallCenter() ? '✅' : '❌'}</div>
          </div>
          <div className={`p-3 rounded text-center ${isProcurement() ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'}`}>
            <div>Procurement</div>
            <div className="font-bold">{isProcurement() ? '✅' : '❌'}</div>
          </div>
          <div className={`p-3 rounded text-center ${isFinance() ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
            <div>Finance</div>
            <div className="font-bold">{isFinance() ? '✅' : '❌'}</div>
          </div>
        </div>
      </div>
      
      {/* Helpdesk Permission Examples */}
      <div className="mb-8">
        <h3 className="font-semibold mb-3">Helpdesk Permission Examples</h3>
        <div className="bg-blue-50 p-4 rounded">
          <h4 className="font-semibold mb-2">Ticket Creation Based on Role:</h4>
          <div className="space-y-2 text-sm">
            {/* Support tickets - anyone can create */}
            <div className="flex items-center justify-between">
              <span>Support Ticket (SUP)</span>
              <span className="text-green-600">✅ Always allowed</span>
            </div>
            
            {/* Enhancement requests - anyone can create */}
            <div className="flex items-center justify-between">
              <span>Enhancement Request (ENH)</span>
              <span className="text-green-600">✅ Always allowed</span>
            </div>
            
            {/* Defect reports - anyone can create */}
            <div className="flex items-center justify-between">
              <span>Defect Report (DEF)</span>
              <span className="text-green-600">✅ Always allowed</span>
            </div>
            
            {/* Staff movement - requires NOT staff (i.e., supervisor+) */}
            <div className="flex items-center justify-between">
              <span>Staff Movement (SMV)</span>
              <span className={!isStaff() ? 'text-green-600' : 'text-red-600'}>
                {!isStaff() ? '✅ Allowed (Supervisor+)' : '❌ Denied (Staff only)'}
              </span>
            </div>
            
            {/* New user requests - requires NOT staff */}
            <div className="flex items-center justify-between">
              <span>New User Request (NEW)</span>
              <span className={!isStaff() ? 'text-green-600' : 'text-red-600'}>
                {!isStaff() ? '✅ Allowed (Supervisor+)' : '❌ Denied (Staff only)'}
              </span>
            </div>
            
            {/* Access termination - requires NOT staff */}
            <div className="flex items-center justify-between">
              <span>Access Termination (TERM)</span>
              <span className={!isStaff() ? 'text-green-600' : 'text-red-600'}>
                {!isStaff() ? '✅ Allowed (Supervisor+)' : '❌ Denied (Staff only)'}
              </span>
            </div>
            
            {/* Access modification - requires NOT staff */}
            <div className="flex items-center justify-between">
              <span>Access Modification (AMD)</span>
              <span className={!isStaff() ? 'text-green-600' : 'text-red-600'}>
                {!isStaff() ? '✅ Allowed (Supervisor+)' : '❌ Denied (Staff only)'}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sample Permissions */}
      <div className="mb-8">
        <h3 className="font-semibold mb-3">Sample Permission Checks</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-semibold mb-2">Procurement Permissions</h4>
            <div className="space-y-2 text-sm">
              <p><strong>View RFQ:</strong> {can('View RFQ') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Create Specification:</strong> {can('Create Specification') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Approve Specification:</strong> {can('Approve Specification') ? '✅ Yes' : '❌ No'}</p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-semibold mb-2">Admin Permissions</h4>
            <div className="space-y-2 text-sm">
              <p><strong>Manage Users:</strong> {can('Manage Users') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>Access Settings:</strong> {can('Access Settings') ? '✅ Yes' : '❌ No'}</p>
              <p><strong>View Assets:</strong> {can('View Assets') ? '✅ Yes' : '❌ No'}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Implementation Notes */}
      <div className="bg-green-50 p-4 rounded">
        <h3 className="font-semibold mb-2 text-green-800">✅ Implementation Complete</h3>
        <div className="text-green-700 text-sm space-y-1">
          <p>• All PHP Utility.php user/permission functions are now available in AuthContext</p>
          <p>• Methods work correctly with masquerading (use effective user, not original user)</p>
          <p>• Sidebar and other components can use these for consistent permission checks</p>
          <p>• Helpdesk ticket creation can use isStaff() to control restricted ticket types</p>
          <p>• Permission-based UI visibility is now centralized and masquerading-aware</p>
        </div>
      </div>
    </div>
  );
}

export default PermissionTest;
